import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ResponsiveLayout } from "@/components/ResponsiveLayout";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "@/lib/queryClient";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { JackpotProvider } from "@/contexts/JackpotContext";
import { PrizeProvider, usePrizes } from "@/contexts/PrizeContext";
import { RecentlyPlayedProvider } from "@/contexts/RecentlyPlayedContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { VipProvider } from "@/contexts/VipContext";
import { BalanceProvider } from "@/contexts/BalanceContext";
import { ModalQueueProvider, useModalQueue } from "@/contexts/ModalQueueContext";
import { JoiningBonusProvider } from "@/contexts/JoiningBonusContext";
import { DailyBonusProvider } from "@/contexts/DailyBonusContext";
import { PolicyAcceptanceProvider } from "@/contexts/PolicyAcceptanceContext";
import { GetCoinsProvider } from "@/contexts/GetCoinsContext";
import { WelcomeOfferProvider } from "@/contexts/WelcomeOfferContext";
import { MonthlyOfferProvider } from "@/contexts/MonthlyOfferContext";
import { WeeklyOfferProvider } from "@/contexts/WeeklyOfferContext";
import { DailyOfferProvider } from "@/contexts/DailyOfferContext";
import { OneTimeOfferProvider } from "@/contexts/OneTimeOfferContext";
import { PWAInstallProvider } from "@/contexts/PWAInstallContext";
import { PushNotificationProvider } from "@/contexts/PushNotificationContext";
import { PaymentProvider } from "@/contexts/PaymentProvider";
import { GeoComplyProvider } from "@/components/GeoComplyProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { CoinflowPurchaseProtection, CoinflowEnvs } from "@coinflowlabs/react";
import { GlobalJoiningBonusModal } from "@/components/GlobalJoiningBonusModal";
import { GlobalDailyBonusModal } from "@/components/GlobalDailyBonusModal";
import { GlobalPolicyAcceptanceModal } from "@/components/GlobalPolicyAcceptanceModal";
import { JackpotWinModal } from "@/components/JackpotWinModal";
import { PrizeWinModal } from "@/components/prizes/PrizeWinModal";
import { PrizePromoPopup, usePrizePromoPopup } from "@/components/prizes/PrizePromoPopup";
import { PromotionPopup, usePromotionPopup } from "@/components/promotions/PromotionPopup";
import { GlobalVipTierUpgradeModal } from "@/components/GlobalVipTierUpgradeModal";
import { GlobalTierExpirationWarningModal } from "@/components/GlobalTierExpirationWarningModal";
import { GlobalGetCoinsModal } from "@/components/GlobalGetCoinsModal";
import { GlobalWelcomeOfferModal } from "@/components/GlobalWelcomeOfferModal";
import { GlobalMonthlyOfferModal } from "@/components/GlobalMonthlyOfferModal";
import { GlobalWeeklyOfferModal } from "@/components/GlobalWeeklyOfferModal";
import { GlobalDailyOfferModal } from "@/components/GlobalDailyOfferModal";
import { GlobalOneTimeOfferModal } from "@/components/GlobalOneTimeOfferModal";
import { GlobalPWAInstallModal } from "@/components/GlobalPWAInstallModal";
import { GlobalPurchaseLimitModal } from "@/components/GlobalPurchaseLimitModal";
import { GlobalScMaxCapModal } from "@/components/GlobalScMaxCapModal";
import { GlobalJackpotOptInPromptModal } from "@/components/GlobalJackpotOptInPromptModal";
import { GlobalPendingRedemptionModal } from "@/components/GlobalPendingRedemptionModal";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PlaythroughGate } from "@/components/PlaythroughGate";
import { useJackpot } from "@/contexts/JackpotContext";
import { VerificationProvider } from "@/contexts/VerificationContext";
import { JackpotOptInPromptProvider } from "@/contexts/JackpotOptInPromptContext";
import { PendingRedemptionProvider } from "@/contexts/PendingRedemptionContext";
import { ChatProvider } from "@/contexts/ChatContext";
import { MaintenanceProvider, useMaintenance } from "@/contexts/MaintenanceContext";
import { useIsBrowserPromptActive } from "@/stores/browserPromptStore";
import { MaintenancePage } from "@/components/MaintenancePage";
import React, { Suspense, lazy } from "react";
import LoadingScreen from "./components/LoadingScreen";

// Lazy load pages for better performance and loading screen display
const LobbyPage = lazy(() => import("./pages/LobbyPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const GetCoinsPage = lazy(() => import("./pages/GetCoinsPage").then(m => ({ default: m.GetCoinsPage })));
const ProfilePage = lazy(() => import("./pages/ProfilePage").then(m => ({ default: m.ProfilePage })));
const PromotionsPage = lazy(() => import("./pages/PromotionsPage").then(m => ({ default: m.PromotionsPage })));
const PromotionDetailPage = lazy(() => import("./pages/PromotionDetailPage").then(m => ({ default: m.PromotionDetailPage })));
const LoyaltyClubPage = lazy(() => import("./pages/LoyaltyClubPage"));
const ForYouPage = lazy(() => import("./pages/ForYouPage").then(m => ({ default: m.ForYouPage })));
const EndlessReelsPage = lazy(() => import("./pages/EndlessReelsPage").then(m => ({ default: m.EndlessReelsPage })));
const LiveExperiencePage = lazy(() => import("./pages/LiveExperiencePage").then(m => ({ default: m.LiveExperiencePage })));
const HighPlayPage = lazy(() => import("./pages/HighPlayPage").then(m => ({ default: m.HighPlayPage })));
const ChallengesPage = lazy(() => import("./pages/ChallengesPage").then(m => ({ default: m.ChallengesPage })));
const InstallAppPage = lazy(() => import("./pages/InstallAppPage").then(m => ({ default: m.InstallAppPage })));
const HelpCenterPage = lazy(() => import("./pages/HelpCenterPage").then(m => ({ default: m.HelpCenterPage })));
const HelpCenterCategoryPage = lazy(() => import("./pages/HelpCenterCategoryPage").then(m => ({ default: m.HelpCenterCategoryPage })));
const HelpCenterArticlePage = lazy(() => import("./pages/HelpCenterArticlePage").then(m => ({ default: m.HelpCenterArticlePage })));
const CMSPageViewPage = lazy(() => import("./pages/CMSPageViewPage").then(m => ({ default: m.CMSPageViewPage })));
const ReferFriendsPage = lazy(() => import("./pages/ReferFriendsPage").then(m => ({ default: m.ReferFriendsPage })));
const DynamicCategoryPage = lazy(() => import("./pages/DynamicCategoryPage").then(m => ({ default: m.DynamicCategoryPage })));
const JackpotPage = lazy(() => import("./pages/JackpotPage").then(m => ({ default: m.JackpotPage })));
const PrizesPage = lazy(() => import("./pages/PrizesPage"));
const PrizeWinnersPage = lazy(() => import("./pages/PrizeWinnersPage"));
const PrizeClaimPage = lazy(() => import("./pages/PrizeClaimPage"));
const GamePlayPage = lazy(() => import("./pages/GamePlayPage").then(m => ({ default: m.GamePlayPage })));
const AmoePage = lazy(() => import("./pages/AmoePage").then(m => ({ default: m.AmoePage })));
const VaultPage = lazy(() => import("./pages/VaultPage").then(m => ({ default: m.VaultPage })));
const ReferralRedirectPage = lazy(() => import("./pages/ReferralRedirectPage"));
const OAuthCallbackPage = lazy(() => import("./pages/OAuthCallbackPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const GlobalJackpotWinModal = () => {
  const { currentWin, markCurrentWinAsSeen } = useJackpot();
  const { addBlocker, removeBlocker } = useModalQueue();
  const isBrowserPromptActive = useIsBrowserPromptActive();
  
  // Block other modals when jackpot win is showing
  React.useEffect(() => {
    if (currentWin) {
      addBlocker('jackpot-win');
      return () => removeBlocker('jackpot-win');
    }
  }, [currentWin, addBlocker, removeBlocker]);
  
  // Wait for any browser permission prompt to be dismissed first
  if (isBrowserPromptActive) return null;
  
  return (
    <JackpotWinModal 
      win={currentWin}
      onClose={markCurrentWinAsSeen}
    />
  );
};

const GlobalPrizeWinModal = () => {
  const { unseenWins, loadPrizes } = usePrizes();
  const { addBlocker, removeBlocker } = useModalQueue();
  const isBrowserPromptActive = useIsBrowserPromptActive();
  const location = useLocation();
  const currentWin = unseenWins[0];
  
  // Don't show modal if we're on the claim page
  const isOnClaimPage = location.pathname.includes('/prizes/claim');
  
  // Block other modals when prize win is showing
  React.useEffect(() => {
    if (currentWin && !isOnClaimPage) {
      addBlocker('prize-win');
      return () => removeBlocker('prize-win');
    }
  }, [currentWin, isOnClaimPage, addBlocker, removeBlocker]);
  
  if (!currentWin || isOnClaimPage || isBrowserPromptActive) return null;
  
  return (
    <PrizeWinModal 
      winner={currentWin}
      onClose={() => loadPrizes()}
    />
  );
};

const GlobalPrizePromoPopup = () => {
  const { showPromo, closePromo } = usePrizePromoPopup();
  const { unseenWins } = usePrizes();
  const { isGloballyBlocked } = useModalQueue();
  const { user } = useAuth();
  const location = useLocation();
  
  // Don't show promo if:
  // - Not ready to show
  // - User is not authenticated or email not verified
  // - User has a win modal pending
  // - Other modals are blocking
  // - Already on prizes page
  const isOnPrizesPage = location.pathname.includes('/prizes');
  const hasUnseenWin = unseenWins.length > 0;
  const isEmailVerified = user?.email_verified === true;
  
  if (!showPromo || !isEmailVerified || hasUnseenWin || isGloballyBlocked || isOnPrizesPage) return null;
  
  return <PrizePromoPopup onClose={closePromo} />;
};

const GlobalPromotionPopup = () => {
  const { showPopup, closePopup, currentPromotion } = usePromotionPopup();
  const { isGloballyBlocked } = useModalQueue();
  const { user } = useAuth();
  const location = useLocation();
  
  // Don't show popup if:
  // - Not ready to show
  // - User is not authenticated or email not verified
  // - Other modals are blocking
  // - Already on promotions page
  // - No promotion to show
  const isOnPromotionsPage = location.pathname.includes('/promotions');
  const isEmailVerified = user?.email_verified === true;
  
  if (!showPopup || !isEmailVerified || isGloballyBlocked || isOnPromotionsPage || !currentPromotion) return null;
  
  return <PromotionPopup onClose={closePopup} promotion={currentPromotion} />;
};

// Coinflow environment based on VITE env var
const coinflowEnv: CoinflowEnvs = import.meta.env.VITE_COINFLOW_ENVIRONMENT === 'production' ? 'prod' : 'sandbox';
const coinflowMerchantId = import.meta.env.VITE_COINFLOW_MERCHANT_ID || '';

/**
 * CoinflowPurchaseProtection wrapper that only renders when user is authenticated.
 * This prevents the nSure SDK from making API calls before the user logs in.
 */
const AuthenticatedCoinflowProtection = () => {
  const { user } = useAuth();
  
  if (!user || !coinflowMerchantId) return null;
  
  return (
    <CoinflowPurchaseProtection 
      coinflowEnv={coinflowEnv} 
      merchantId={coinflowMerchantId} 
    />
  );
};

// Wrapper component for routes with error boundary
const RouteWithErrorBoundary = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary>{children}</ErrorBoundary>
);

const SimpleLoadingScreen = () => (
  <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[hsl(220,20%,8%)]">
    <div className="relative">
      <div 
        className="absolute inset-0 blur-2xl opacity-30"
        style={{
          background: 'radial-gradient(circle, rgba(251,191,36,0.4) 0%, transparent 70%)',
          width: 240,
          height: 240,
          left: -60,
          top: -60,
        }}
      />
      <img
        src="/gold-machine-logo.png"
        alt="Gold Machine"
        width={120}
        height={120}
        className="relative z-10 animate-pulse object-contain"
        style={{ animationDuration: '2s' }}
      />
    </div>
    <div className="flex items-center gap-1 mt-6">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-amber-500/60"
          style={{
            animation: 'dot-pulse 1.2s infinite ease-in-out',
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </div>
    <style>{`
      @keyframes dot-pulse {
        0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
        40% { opacity: 1; transform: scale(1); }
      }
    `}</style>
  </div>
);

const MaintenanceBypassBanner = () => {
  const { isBypassed, disableBypass } = useMaintenance();

  if (!isBypassed) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[9998] bg-amber-500 text-black px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-3">
      <span>Maintenance Mode Bypass Active</span>
      <button
        onClick={() => {
          disableBypass();
          window.location.reload();
        }}
        className="px-2 py-0.5 bg-black/20 hover:bg-black/30 rounded text-xs transition-colors"
      >
        Disable Bypass
      </button>
    </div>
  );
};

const MaintenanceGate = ({ children }: { children: React.ReactNode }) => {
  const { isMaintenanceMode, isLoading, isBypassed } = useMaintenance();

  if (isLoading) {
    return <SimpleLoadingScreen />;
  }

  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  return (
    <>
      {isBypassed && <MaintenanceBypassBanner />}
      <div className={isBypassed ? 'pt-10' : ''}>
        {children}
      </div>
    </>
  );
};

// Wrapper route for CMS pages so that:
// - Logged-out users see the public/landing-style layout
// - Logged-in users see the authenticated app layout (ResponsiveLayout)
const CMSPageRouteWrapper = () => {
  const { user } = useAuth();

  if (user) {
    return (
      <ResponsiveLayout>
        <CMSPageViewPage />
      </ResponsiveLayout>
    );
  }

  return <CMSPageViewPage />;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
    <MaintenanceProvider>
    <MaintenanceGate>
    <AuthProvider>
            {/* CoinflowPurchaseProtection - Only load after user authenticates */}
            {/* This loads the nSure SDK to gather device/session data for fraud detection */}
            <AuthenticatedCoinflowProtection />
            <BalanceProvider>
              <VipProvider>
                <ThemeProvider>
                    <ModalQueueProvider>
                      <JoiningBonusProvider>
                        <PolicyAcceptanceProvider>
                            <WelcomeOfferProvider>
                            <MonthlyOfferProvider>
                            <WeeklyOfferProvider>
                            <DailyOfferProvider>
                            <OneTimeOfferProvider>
                            <PWAInstallProvider>
                            <PushNotificationProvider>
                            <RecentlyPlayedProvider>
                              <JackpotProvider>
                              <PrizeProvider>
                                <GeoComplyProvider>
                                <TooltipProvider>
                                  <Toaster />
                                  <Sonner />
                                  <BrowserRouter>
                                <JackpotOptInPromptProvider>
                                <ChatProvider>
                                    <ErrorBoundary>
                                      <GlobalJackpotWinModal />
                                      <GlobalPrizeWinModal />
                                      <GlobalPrizePromoPopup />
                                      <GlobalPromotionPopup />
                                    </ErrorBoundary>
                                    <ScrollToTop />
                                    <DailyBonusProvider>
                                    <GetCoinsProvider>
                                    <VerificationProvider>
                                      <PaymentProvider>
                                      <ErrorBoundary>
                                        <GlobalPolicyAcceptanceModal />
                                      </ErrorBoundary>
                                      <ErrorBoundary>
                                        <GlobalJoiningBonusModal />
                                      </ErrorBoundary>
                                      <ErrorBoundary>
                                        <GlobalDailyBonusModal />
                                      </ErrorBoundary>
                                      <ErrorBoundary>
                                        <GlobalVipTierUpgradeModal />
                                      </ErrorBoundary>
                                      <ErrorBoundary>
                                        <GlobalTierExpirationWarningModal />
                                      </ErrorBoundary>
                                      <ErrorBoundary>
                                        <GlobalGetCoinsModal />
                                      </ErrorBoundary>
                                      <ErrorBoundary>
                                        <GlobalWelcomeOfferModal />
                                      </ErrorBoundary>
                                      <ErrorBoundary>
                                        <GlobalMonthlyOfferModal />
                                      </ErrorBoundary>
                                      <ErrorBoundary>
                                        <GlobalWeeklyOfferModal />
                                      </ErrorBoundary>
                                      <ErrorBoundary>
                                        <GlobalDailyOfferModal />
                                      </ErrorBoundary>
                                      <ErrorBoundary>
                                        <GlobalOneTimeOfferModal />
                                      </ErrorBoundary>
                                      <ErrorBoundary>
                                        <GlobalPWAInstallModal />
                                      </ErrorBoundary>
                                      <ErrorBoundary>
                                        <GlobalPurchaseLimitModal />
                                      </ErrorBoundary>
                                      <ErrorBoundary>
                                        <GlobalScMaxCapModal />
                                      </ErrorBoundary>
                                      <ErrorBoundary>
                                        <GlobalJackpotOptInPromptModal />
                                      </ErrorBoundary>
                                      <PendingRedemptionProvider>
                                      <ErrorBoundary>
                                        <GlobalPendingRedemptionModal />
                                      </ErrorBoundary>
                                      <Suspense fallback={<LoadingScreen />}>
                                        <Routes>
                                          {/* Routes WITHOUT persistent layout (full-screen pages) */}
                                          <Route path="/" element={<RouteWithErrorBoundary><AuthPage /></RouteWithErrorBoundary>} />
                                          <Route path="/ref/:code" element={<RouteWithErrorBoundary><ReferralRedirectPage /></RouteWithErrorBoundary>} />
                                          <Route path="/oauth/google/callback" element={<OAuthCallbackPage />} />
                                          <Route path="/reset-password" element={<RouteWithErrorBoundary><ResetPasswordPage /></RouteWithErrorBoundary>} />
                                          <Route path="/verify-email" element={<RouteWithErrorBoundary><VerifyEmailPage /></RouteWithErrorBoundary>} />
                                          <Route path="/get-coins" element={<ProtectedRoute><RouteWithErrorBoundary><GetCoinsPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                          <Route path="/play/:gameId" element={<ProtectedRoute><RouteWithErrorBoundary><GamePlayPage /></RouteWithErrorBoundary></ProtectedRoute>} />

                                          {/* CMS Pages - clean URLs like /terms-of-service, with layout depending on auth state */}
                                          <Route path="/:slug" element={<RouteWithErrorBoundary><CMSPageRouteWrapper /></RouteWithErrorBoundary>} />

                                          {/* Routes WITH persistent layout - layout persists across navigation for app-like experience */}
                                          <Route element={<ResponsiveLayout />}>
                                            <Route path="/home" element={<ProtectedRoute><RouteWithErrorBoundary><LobbyPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/profile" element={<ProtectedRoute><RouteWithErrorBoundary><ProfilePage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/promotions" element={<ProtectedRoute><RouteWithErrorBoundary><PromotionsPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/promotions/:slug" element={<ProtectedRoute><RouteWithErrorBoundary><PromotionDetailPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/loyalty-club" element={<ProtectedRoute><RouteWithErrorBoundary><LoyaltyClubPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/for-you" element={<ProtectedRoute><RouteWithErrorBoundary><ForYouPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/endless-reels" element={<ProtectedRoute><RouteWithErrorBoundary><EndlessReelsPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/live-experience" element={<ProtectedRoute><RouteWithErrorBoundary><PlaythroughGate section="live"><LiveExperiencePage /></PlaythroughGate></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/high-play" element={<ProtectedRoute><RouteWithErrorBoundary><PlaythroughGate section="high_play"><HighPlayPage /></PlaythroughGate></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/challenges" element={<ProtectedRoute><RouteWithErrorBoundary><ChallengesPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            {/* Legacy redirects */}
                                            <Route path="/casino-games" element={<Navigate to="/endless-reels" replace />} />
                                            <Route path="/spin-and-win" element={<Navigate to="/endless-reels" replace />} />
                                            <Route path="/live-casino" element={<Navigate to="/live-experience" replace />} />
                                            <Route path="/fun-games" element={<Navigate to="/high-play" replace />} />
                                            <Route path="/challenge-zone" element={<Navigate to="/high-play" replace />} />
                                            <Route path="/install-app" element={<ProtectedRoute><RouteWithErrorBoundary><InstallAppPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/help-center" element={<ProtectedRoute><RouteWithErrorBoundary><HelpCenterPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/help-center/category/:slug" element={<ProtectedRoute><RouteWithErrorBoundary><HelpCenterCategoryPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/help-center/article/:slug" element={<ProtectedRoute><RouteWithErrorBoundary><HelpCenterArticlePage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/amoe" element={<ProtectedRoute><RouteWithErrorBoundary><AmoePage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/vault" element={<ProtectedRoute><RouteWithErrorBoundary><VaultPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/refer-friends" element={<ProtectedRoute><RouteWithErrorBoundary><ReferFriendsPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/game/categories/:slug" element={<ProtectedRoute><RouteWithErrorBoundary><DynamicCategoryPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/jackpot" element={<ProtectedRoute><RouteWithErrorBoundary><JackpotPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/prizes" element={<ProtectedRoute><RouteWithErrorBoundary><PrizesPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/prize-winners" element={<ProtectedRoute><RouteWithErrorBoundary><PrizeWinnersPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="/prizes/claim/:winnerId" element={<ProtectedRoute><RouteWithErrorBoundary><PrizeClaimPage /></RouteWithErrorBoundary></ProtectedRoute>} />
                                            <Route path="*" element={<RouteWithErrorBoundary><NotFound /></RouteWithErrorBoundary>} />
                                          </Route>
                                        </Routes>
                                      </Suspense>
                                      </PendingRedemptionProvider>
                                      </PaymentProvider>
                                    </VerificationProvider>
                                    </GetCoinsProvider>
                                    </DailyBonusProvider>
                                </ChatProvider>
                                </JackpotOptInPromptProvider>
                                  </BrowserRouter>
                                </TooltipProvider>
                                </GeoComplyProvider>
                              </PrizeProvider>
                              </JackpotProvider>
                            </RecentlyPlayedProvider>
                            </PushNotificationProvider>
                            </PWAInstallProvider>
                            </OneTimeOfferProvider>
                            </DailyOfferProvider>
                            </WeeklyOfferProvider>
                            </MonthlyOfferProvider>
                            </WelcomeOfferProvider>
                        </PolicyAcceptanceProvider>
                      </JoiningBonusProvider>
                    </ModalQueueProvider>
                </ThemeProvider>
              </VipProvider>
            </BalanceProvider>
        </AuthProvider>
    </MaintenanceGate>
    </MaintenanceProvider>
    <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
      </ErrorBoundary>
);

export default App;
